import build from './buildVersion.json';

const APP = 'incidentalert';
let apiBase = (process.env.REACT_APP_API_BASE || '/api').replace(/\/+$/, '');
const storageKey = `pwa-diagnostics:${APP}`;
const events = new Set(('app_started push_unavailable permission_result enrollment_ok enrollment_failed ' +
  'logout_cleanup_ok logout_cleanup_failed test_ok test_failed worker_registration_ok worker_registration_failed ' +
  'update_available update_deferred update_reload update_check_failed update_check_recovered update_applied update_still_stale').split(' '));
let state;
let timer;
let sending = false;
let nextSendAt = 0;

function token(value, max = 48) {
  return typeof value === 'string' && /^[A-Za-z0-9_.-]+$/.test(value) ? value.slice(0, max) : undefined;
}

export function diagnosticError(error) {
  // Never include messages/stacks: those can contain URLs, credentials or chat text.
  try {
    const status = Number(error && (error.status || error.statusCode || error.response?.status));
    return { error: token(error?.code) || token(error?.name) || 'Error',
      ...(status >= 100 && status <= 599 ? { status } : {}) };
  } catch (_) { return { error: 'Error' }; }
}

export function configurePwaDiagnostics(value) {
  try { if (typeof value === 'string' && value) apiBase = value.replace(/\/+$/, ''); } catch (_) {}
}

function persist() {
  try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) { /* Private mode: memory only. */ }
}

function load() {
  if (state) return;
  const bytes = new Uint8Array(8);
  try { window.crypto.getRandomValues(bytes); }
  catch (_) { for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256); }
  state = { clientId: 'p' + Array.from(bytes, n => n.toString(16).padStart(2, '0')).join(''),
    day: '', count: 0, seen: [], records: [] };
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (saved && /^p[a-f0-9]{16}$/.test(saved.clientId)) {
      state.clientId = saved.clientId;
      state.day = String(saved.day || '').slice(0, 10);
      state.count = Math.max(0, Math.min(60, Number(saved.count) || 0));
      state.seen = Array.isArray(saved.seen) ? saved.seen.filter(pair => Array.isArray(pair) &&
        typeof pair[0] === 'string' && pair[0].length < 240 && Number.isFinite(pair[1])).slice(-40) : [];
      state.records = Array.isArray(saved.records) ? saved.records.filter(record => record &&
        events.has(record.event) && record.app === APP && Number.isFinite(record.at) &&
        Date.now() - record.at < 86400000 && JSON.stringify(record).length <= 800).slice(-20) : [];
    }
  } catch (_) { /* A blocked/corrupt diagnostics buffer must never affect the app. */ }
}

function schedule() {
  if (timer || sending || navigator.onLine === false) return;
  timer = window.setTimeout(() => { timer = null; void flush(); }, Math.max(1000, nextSendAt - Date.now()));
}

async function flush() {
  if (sending || navigator.onLine === false || !state) return;
  const batch = state.records.filter(record => !record.sent).slice(0, 4);
  if (!batch.length) return;
  if (Date.now() < nextSendAt) { schedule(); return; }
  sending = true;
  nextSendAt = Date.now() + 10000;
  let controller;
  let timeout;
  let accepted = false;
  try {
    controller = new AbortController();
    timeout = window.setTimeout(() => controller.abort(), 3000);
    const response = await fetch(`${apiBase.replace(/\/+$/, '')}/pwa-diagnostics`, {
      method: 'POST', credentials: 'omit', cache: 'no-store', keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ events: batch.map(({ sent, ...record }) => record) }), signal: controller.signal,
    });
    // A rejected diagnostic is disposable; do not retry it in a tight loop.
    accepted = response.ok || (response.status >= 400 && response.status < 500);
    if (accepted) {
      batch.forEach(record => { record.sent = true; });
      persist();
    }
  } catch (_) { /* Keep at most 20 records; retry only on a later event/reconnect. */ }
  finally {
    if (timeout) window.clearTimeout(timeout);
    sending = false;
    if (accepted && state.records.some(record => !record.sent)) schedule();
  }
}

export function reportPwaDiagnostic(event, details = {}) {
  try {
    if (!events.has(event) || process.env.REACT_APP_PWA_DIAGNOSTICS === 'false') return;
    load();
    const now = Date.now();
    const day = new Date(now).toISOString().slice(0, 10);
    if (state.day !== day) { state.day = day; state.count = 0; }
    if (state.count >= 60) { schedule(); return; }
    const record = { app: APP, source: 'page', event, at: now, clientId: state.clientId,
      build: build.version, online: navigator.onLine !== false,
      standalone: Boolean(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone) };
    for (const key of ['phase', 'error', 'workerState']) {
      const value = token(details[key]);
      if (value) record[key] = value;
    }
    if (['granted', 'denied', 'default'].includes(details.permission)) record.permission = details.permission;
    if (Number.isInteger(details.status) && details.status >= 100 && details.status <= 599) record.status = details.status;
    if (/^[a-f0-9]{24}$/.test(details.remoteBuild || '')) record.remoteBuild = details.remoteBuild;
    if (typeof details.controlled === 'boolean') record.controlled = details.controlled;
    const ua = navigator.userAgent || '';
    record.browser = /Firefox/i.test(ua) ? 'firefox' : /Edg/i.test(ua) ? 'edge' : /Chrome|CriOS/i.test(ua) ? 'chrome' : /Safari/i.test(ua) ? 'safari' : 'other';
    record.platform = /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      ? 'ios' : /Android/i.test(ua) ? 'android' : 'desktop';
    const signature = [event, record.phase, record.error, record.status, record.permission, record.remoteBuild, build.version].join(':');
    const previous = state.seen.find(pair => pair[0] === signature);
    if (previous && now - previous[1] < 15 * 60000) { schedule(); return; }
    state.seen = state.seen.filter(pair => pair[0] !== signature && now - pair[1] < 15 * 60000);
    state.seen.push([signature, now]);
    state.seen = state.seen.slice(-40);
    state.count++;
    state.records = state.records.filter(entry => now - entry.at < 86400000).slice(-19);
    state.records.push(record);
    persist();
    schedule();
  } catch (_) { /* Diagnostics cannot interrupt enrollment, logout or updates. */ }
}

// No polling or retry loop for telemetry. A reconnect can upload the small buffer.
try {
  window.addEventListener('online', () => { try { load(); schedule(); } catch (_) {} });
} catch (_) {}
